import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { Loader2, Sparkles, Check, Save, ArrowRight, Target, Lock, Gauge } from "lucide-react";
import DeliverableFeedback from "@/components/DeliverableFeedback";
import api, { formatApiError } from "@/lib/api";
import { useWorkspace } from "@/lib/workspace-context";
import { getActiveWorkspaceId } from "@/lib/active-workspace";
import { useEntitlement } from "@/lib/entitlement-context";
import UpgradeLiveModal from "@/components/UpgradeLiveModal";

const PREP_BATCHES = [
  { id: 1, label: "Introduction & Motivation" },
  { id: 2, label: "Skills & Experience" },
  { id: 3, label: "Behavioral, Situational & Role Fit" },
];

export default function InterviewPrep({
  kicker = "02 · Predict & Prepare",
  title = "What might they ask - and what will you say?",
  intro = "Predict the questions this role will throw at you, then draft and refine your answers.",
} = {}) {
  const [searchParams] = useSearchParams();
  const analysisIdParam = searchParams.get("analysis_id");
  const questionSetIdParam = searchParams.get("question_set_id");
  const [analyses, setAnalyses] = useState([]);
  const [questionSets, setQuestionSets] = useState([]);
  const [selectedAnalysisId, setSelectedAnalysisId] = useState(analysisIdParam || "");
  const [generating, setGenerating] = useState(false);
  const [activeSet, setActiveSet] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const { isFree, track, refresh: refreshEntitlement } = useEntitlement();
  const [answers, setAnswers] = useState({});
  const [savedAnswers, setSavedAnswers] = useState({});
  const [busyAnswer, setBusyAnswer] = useState({});
  const [grades, setGrades] = useState({}); // { [i]: { score, tip } }
  const [busyGrade, setBusyGrade] = useState({});
  const [error, setError] = useState("");
  const [curIdx, setCurIdx] = useState(() => Math.max(0, parseInt(searchParams.get("q") || "0", 10) || 0));
  const autosaveTimer = useRef(null);
  const [autosaveState, setAutosaveState] = useState("");
  const navigate = useNavigate();
  const { activeId: wsActiveId, loading: wsLoading } = useWorkspace();

  // Load workspace-scoped data only once the active workspace has hydrated
  // (so the X-Workspace-Id header is guaranteed on every request), and
  // reload from scratch whenever the user switches workspaces.
  useEffect(() => {
    if (wsLoading) return;
    let cancelled = false;
    (async () => {
      try {
        const [a, qs] = await Promise.all([
          api.get("/resume/analyses"),
          api.get("/interview/questions/sets"),
        ]);
        if (cancelled) return;
        const analysesList = a.data || [];
        const sets = qs.data || [];
        setAnalyses(analysesList);
        setQuestionSets(sets);
        setSelectedAnalysisId(
          analysisIdParam && analysesList.some((x) => x.analysis_id === analysisIdParam)
            ? analysisIdParam
            : analysesList[0]?.analysis_id || ""
        );
        // Load the most recent saved question set immediately — no waiting,
        // no regeneration. When arriving from a specific analysis, only
        // auto-open a set that belongs to that analysis.
        const preferred =
          (questionSetIdParam && sets.find((s) => s.question_set_id === questionSetIdParam)) ||
          (analysisIdParam
            ? sets.find((s) => s.analysis_id === analysisIdParam)
            : sets[0]);
        if (preferred) openSet(preferred.question_set_id);
        else setActiveSet(null);
      } catch {
        /* transient — page renders empty state */
      }
    })();
    return () => { cancelled = true; };
  }, [wsLoading, wsActiveId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeSet) {
      setAnswers(activeSet.answers || {});
      setSavedAnswers(activeSet.answers || {});
      setGrades(activeSet.grades || {});
    } else {
      setAnswers({});
      setSavedAnswers({});
      setGrades({});
    }
  }, [activeSet]);

  const generate = async () => {
    setError("");
    if (!selectedAnalysisId) {
      setError("Pick an analysis first, or run Analyze Fit in Stand Out.");
      return;
    }
    setGenerating(true);
    try {
      const { data } = await api.post("/interview/questions/generate", {
        analysis_id: selectedAnalysisId,
        count: 15,
      });
      setActiveSet(data);
      const list = await api.get("/interview/questions/sets");
      setQuestionSets(list.data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setGenerating(false);
    }
  };

  const openSet = async (id) => {
    try {
      const { data } = await api.get(`/interview/questions/sets/${id}`);
      // Guard: never render a set that belongs to a different workspace
      // (possible with a stale id right after a workspace switch).
      const ws = getActiveWorkspaceId();
      if (data?.workspace_id && ws && data.workspace_id !== ws) return;
      setActiveSet(data);
      track("predicted.viewed", {
        question_set_id: id,
        visible: (data?.questions || []).length,
        locked_count: data?.locked_count || 0,
        total_available: data?.total_available || (data?.questions || []).length,
      });
      if ((data?.locked_count || 0) > 0) {
        track("predicted.locked_teaser_seen", { locked_count: data.locked_count });
      }
    } catch {
      /* 404/409 — the set isn't in the active workspace */
    }
  };

  const generateAnswer = async (index) => {
    if (!activeSet) return;
    setBusyAnswer((s) => ({ ...s, [index]: true }));
    try {
      const { data } = await api.post("/interview/questions/answer", {
        question_set_id: activeSet.question_set_id,
        question_index: index,
      });
      setAnswers((prev) => ({ ...prev, [index]: data.answer }));
      autosave(index, data.answer);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusyAnswer((s) => ({ ...s, [index]: false }));
    }
  };

  // Autosave (Step 03): quietly persist the draft ~1.2s after typing pauses,
  // so no answer is ever lost. Explicit Save stays — it also triggers grading.
  const autosave = async (index, text) => {
    try {
      setAutosaveState("saving");
      await api.post("/interview/questions/save-answer", {
        question_set_id: activeSet.question_set_id,
        question_index: index,
        answer: text,
      });
      setSavedAnswers((s) => ({ ...s, [index]: text }));
      setAutosaveState("saved");
    } catch {
      setAutosaveState("");
    }
  };

  const onAnswerChange = (index, text) => {
    setAnswers((s) => ({ ...s, [index]: text }));
    clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => autosave(index, text), 1200);
  };

  const saveAnswer = async (index) => {
    if (!activeSet) return;
    setBusyAnswer((s) => ({ ...s, [`save-${index}`]: true }));
    try {
      await api.post("/interview/questions/save-answer", {
        question_set_id: activeSet.question_set_id,
        question_index: index,
        answer: answers[index] || "",
      });
      setSavedAnswers((s) => ({ ...s, [index]: answers[index] || "" }));
      // Premium users get an automatic AI Quality Score after save.
      // Free users see a blurred lock chip - no LLM call is issued.
      if (!isFree && (answers[index] || "").trim().length >= 20) {
        gradeAnswer(index);
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusyAnswer((s) => ({ ...s, [`save-${index}`]: false }));
    }
  };

  const gradeAnswer = async (index) => {
    if (!activeSet) return;
    setBusyGrade((s) => ({ ...s, [index]: true }));
    try {
      const { data } = await api.post("/interview/questions/grade-answer", {
        question_set_id: activeSet.question_set_id,
        question_index: index,
        answer: answers[index] || "",
      });
      setGrades((s) => ({ ...s, [index]: { score: data.score, tip: data.tip } }));
      track("predicted.answer_graded", {
        score: data.score,
        question_set_id: activeSet.question_set_id,
      });
    } catch (err) {
      // 402 → premium required; frontend already renders lock chip for free users.
      if (err.response?.status !== 402) {
        setError(formatApiError(err.response?.data?.detail) || err.message);
      }
    } finally {
      setBusyGrade((s) => ({ ...s, [index]: false }));
    }
  };

  const startMock = () => {
    if (!activeSet) return;
    navigate(`/app/practice?question_set_id=${activeSet.question_set_id}`);
  };

  const answeredCount = Object.values(savedAnswers).filter((a) => (a || "").trim().length > 20).length;
  const totalQuestions = activeSet?.questions?.length || 0;
  const readiness = totalQuestions ? Math.round((answeredCount / totalQuestions) * 100) : 0;
  const allPrepared = totalQuestions > 0 && answeredCount >= totalQuestions;
  const safeIdx = Math.min(curIdx, Math.max(totalQuestions - 1, 0));
  const curQuestion = activeSet?.questions?.[safeIdx];

  return (
    <div data-testid="interview-prep-page" className="animate-in">
      <div className="mb-8">
        {kicker && (
          <div className="text-[11px] uppercase tracking-[0.24em] text-amber-400 mb-2">
            {kicker}
          </div>
        )}
        <h1 className="text-3xl font-bold tracking-tight text-zinc-50">
          {title}
        </h1>
        <p className="text-sm text-zinc-500 mt-2">
          {intro}
        </p>
      </div>

      {error && (
        <div
          data-testid="prep-error"
          className="mb-6 text-sm text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded-lg px-4 py-3"
        >
          {error}
        </div>
      )}

      {/* Generator */}
      <div className="p-5 rounded-xl border border-zinc-800/70 bg-zinc-900/40 mb-6">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[240px]">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
              Source (from your Fit Analysis)
            </div>
            <select
              data-testid="prep-analysis-select"
              value={selectedAnalysisId}
              onChange={(e) => setSelectedAnalysisId(e.target.value)}
              className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-50 focus:outline-none focus:border-zinc-700"
            >
              <option value="">Select an analysis…</option>
              {analyses.map((a) => (
                <option key={a.analysis_id} value={a.analysis_id}>
                  {a.job_title || "Role"} {a.company ? `@ ${a.company}` : ""}
                </option>
              ))}
            </select>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Questions</div>
            <div
              data-testid="prep-bank-note"
              className="text-xs text-zinc-400 bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2.5 leading-snug"
            >
              <span className="text-zinc-100 font-semibold">15 questions</span> · 3 batches of 5
            </div>
          </div>
          <button
            data-testid="prep-generate-btn"
            onClick={generate}
            disabled={generating || !selectedAnalysisId}
            className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-amber-950 disabled:opacity-60 font-semibold rounded-lg px-4 py-2.5"
          >
            {generating ? (
              <><Loader2 className="h-4 w-4 animate-spin" /> Predicting…</>
            ) : (
              <><Sparkles className="h-4 w-4" /> Predict questions</>
            )}
          </button>
        </div>
        {analyses.length === 0 && (
          <div className="mt-4 text-xs text-zinc-500">
            No analyses yet.{" "}
            <Link to="/app/resume" className="text-amber-400 hover:text-amber-300">
              Go to Stand Out
            </Link>
            .
          </div>
        )}
      </div>

      {/* Previous sets */}
      {questionSets.length > 0 && !activeSet && (
        <div className="mb-6">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3">
            Saved question sets
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {questionSets.map((qs) => (
              <button
                key={qs.question_set_id}
                data-testid={`prep-set-${qs.question_set_id}`}
                onClick={() => openSet(qs.question_set_id)}
                className="text-left p-4 rounded-lg border border-zinc-800 bg-zinc-900/40 hover:border-zinc-700 hover:-translate-y-0.5 transition-all"
              >
                <div className="text-sm font-medium text-zinc-100">
                  {qs.job_title || "Interview questions"}
                </div>
                <div className="text-xs text-zinc-500 mt-1">
                  {qs.questions?.length || 0} questions
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Active set · Predict & Prepare: question navigator + answer editor */}
      {activeSet && (
        <div className="animate-in">
          {/* Progress + Mock Interview gate */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4 p-4 rounded-lg border border-zinc-800 bg-zinc-900/40">
            <div className="flex items-center gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Preparation</div>
                <div data-testid="prepare-progress" className="text-sm text-zinc-100 font-medium">
                  {answeredCount} / {totalQuestions} answers prepared
                </div>
              </div>
              <div className="w-40 h-1.5 rounded-full bg-zinc-800 overflow-hidden">
                <div className="h-full bg-amber-500 transition-all" style={{ width: `${readiness}%` }} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setActiveSet(null)}
                className="text-xs border border-zinc-800 text-zinc-400 hover:text-zinc-100 rounded-md px-3 py-1.5"
              >
                Back to sets
              </button>
              {allPrepared ? (
                <button
                  data-testid="prep-practice-answers-btn"
                  onClick={startMock}
                  className="inline-flex items-center gap-1.5 text-xs bg-amber-500 hover:bg-amber-400 text-amber-950 font-semibold rounded-md px-3 py-1.5"
                >
                  <Target className="h-3 w-3" /> Practice Your Answers <ArrowRight className="h-3 w-3" />
                </button>
              ) : (
                <div data-testid="prepare-mock-hint" className="text-[11px] text-zinc-500">
                  Prepare all answers to unlock Practice
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[300px,1fr] gap-4 items-start">
            {/* Question navigator — grouped by the 3 master-bank batches */}
            <div data-testid="prepare-question-nav" className="space-y-3">
              {PREP_BATCHES.map((b) => {
                const items = activeSet.questions
                  .map((q, i) => ({ q, i }))
                  .filter(({ q, i }) => (q.batch || Math.min(Math.floor(i / 5) + 1, 3)) === b.id);
                if (!items.length) return null;
                const preparedInBatch = items.filter(({ i }) => (savedAnswers[i] || "").trim().length > 20).length;
                return (
                  <div key={b.id} data-testid={`prepare-batch-${b.id}`}>
                    <div className="flex items-center justify-between px-1 mb-1.5">
                      <span className="text-[10px] uppercase tracking-widest text-zinc-500">
                        Batch {b.id} · {b.label}
                      </span>
                      <span className="text-[10px] text-zinc-600 tabular-nums" data-testid={`prepare-batch-${b.id}-progress`}>
                        {preparedInBatch}/{items.length}
                      </span>
                    </div>
                    <div className="space-y-1.5">
                      {items.map(({ q, i }) => {
                        const prepared = (savedAnswers[i] || "").trim().length > 20;
                        const active = i === safeIdx;
                        return (
                          <button
                            key={i}
                            type="button"
                            onClick={() => setCurIdx(i)}
                            data-testid={`prepare-nav-${i}`}
                            className={`w-full flex items-start gap-2.5 text-left rounded-lg border px-3 py-2.5 transition-colors ${
                              active
                                ? "border-amber-500/40 bg-amber-500/10"
                                : "border-zinc-800 bg-zinc-900/40 hover:border-zinc-700"
                            }`}
                          >
                            <span className={`text-[10px] font-mono mt-0.5 tabular-nums ${active ? "text-amber-400" : "text-zinc-600"}`}>
                              {String(i + 1).padStart(2, "0")}
                            </span>
                            <span className={`flex-1 text-xs leading-snug line-clamp-2 ${active ? "text-zinc-50" : "text-zinc-400"}`}>
                              {q.question}
                            </span>
                            {prepared && <Check className="h-3.5 w-3.5 text-emerald-400 flex-shrink-0 mt-0.5" />}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Current question editor */}
            {curQuestion && (
              <div data-testid="prepare-current-question" className="border border-zinc-800 rounded-lg bg-zinc-900/40 p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <span className="text-[10px] font-mono text-zinc-600 mt-1 tabular-nums">
                    {String(safeIdx + 1).padStart(2, "0")}
                  </span>
                  <div className="min-w-0">
                    <div className="text-sm text-zinc-100 leading-relaxed">{curQuestion.question}</div>
                    <div className="flex items-center gap-2 mt-2 text-[10px] uppercase tracking-widest flex-wrap">
                      <span
                        data-testid="prep-current-batch"
                        className="px-1.5 py-0.5 rounded text-amber-400 border border-amber-500/30 bg-amber-500/5"
                      >
                        Batch {curQuestion.batch || Math.min(Math.floor(safeIdx / 5) + 1, 3)} ·{" "}
                        {(PREP_BATCHES.find((b) => b.id === (curQuestion.batch || Math.min(Math.floor(safeIdx / 5) + 1, 3))) || {}).label}
                      </span>
                      <span className="px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400 border border-zinc-700">
                        {curQuestion.category}
                      </span>
                      <span
                        className={`px-1.5 py-0.5 rounded border ${
                          curQuestion.difficulty === "Hard"
                            ? "text-rose-400 border-rose-500/30 bg-rose-500/5"
                            : curQuestion.difficulty === "Medium"
                            ? "text-amber-400 border-amber-500/30 bg-amber-500/5"
                            : "text-emerald-400 border-emerald-500/30 bg-emerald-500/5"
                        }`}
                      >
                        {curQuestion.difficulty}
                      </span>
                    </div>
                  </div>
                </div>
                {(curQuestion.why_likely || curQuestion.why_asked) && (
                  <div className="text-xs text-zinc-400 italic leading-relaxed">
                    Why likely: {curQuestion.why_likely || curQuestion.why_asked}
                  </div>
                )}
                <textarea
                  data-testid={`prep-answer-${safeIdx}`}
                  rows={8}
                  placeholder="Your answer…"
                  value={answers[safeIdx] || ""}
                  onChange={(e) => onAnswerChange(safeIdx, e.target.value)}
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-zinc-700 focus:outline-none leading-relaxed"
                />
                <div className="flex gap-2 flex-wrap items-center">
                  <button
                    data-testid={`prep-generate-answer-${safeIdx}`}
                    onClick={() => generateAnswer(safeIdx)}
                    disabled={busyAnswer[safeIdx]}
                    className="inline-flex items-center gap-1.5 text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-100 font-medium rounded-md px-3 py-1.5"
                  >
                    {busyAnswer[safeIdx] ? (
                      <><Loader2 className="h-3 w-3 animate-spin" /> Drafting…</>
                    ) : (
                      <><Sparkles className="h-3 w-3" /> AI draft</>
                    )}
                  </button>
                  <button
                    data-testid={`prep-save-answer-${safeIdx}`}
                    onClick={() => saveAnswer(safeIdx)}
                    disabled={busyAnswer[`save-${safeIdx}`]}
                    className="inline-flex items-center gap-1.5 text-xs bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold rounded-md px-3 py-1.5"
                  >
                    {busyAnswer[`save-${safeIdx}`] ? (
                      <><Loader2 className="h-3 w-3 animate-spin" /> Saving…</>
                    ) : (
                      <><Save className="h-3 w-3" /> Save</>
                    )}
                  </button>
                  {autosaveState === "saving" && (
                    <span data-testid="prepare-autosave-status" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-500">
                      <Loader2 className="h-3 w-3 animate-spin" /> Saving…
                    </span>
                  )}
                  {autosaveState === "saved" && (
                    <span data-testid="prepare-autosave-status" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-500">
                      <Check className="h-3 w-3 text-emerald-400" /> Auto-saved
                    </span>
                  )}
                  <AnswerScoreChip
                    index={safeIdx}
                    grade={grades[safeIdx]}
                    busy={busyGrade[safeIdx]}
                    isFree={isFree}
                    answerText={answers[safeIdx] || ""}
                    onUpgrade={() => {
                      track("predicted.locked_score_clicked", {
                        question_set_id: activeSet.question_set_id,
                        question_index: safeIdx,
                      });
                      navigate("/app/upgrade");
                    }}
                  />
                </div>
                <div className="flex items-center justify-between pt-3 border-t border-zinc-800">
                  <button
                    type="button"
                    data-testid="prepare-prev-btn"
                    onClick={() => setCurIdx(Math.max(safeIdx - 1, 0))}
                    disabled={safeIdx === 0}
                    className="text-xs border border-zinc-800 text-zinc-400 hover:text-zinc-100 disabled:opacity-40 disabled:hover:text-zinc-400 rounded-md px-3 py-1.5"
                  >
                    ← Previous
                  </button>
                  <span className="text-[10px] text-zinc-600 font-mono tabular-nums">
                    {safeIdx + 1} / {totalQuestions}
                  </span>
                  <button
                    type="button"
                    data-testid="prepare-next-btn"
                    onClick={() => setCurIdx(Math.min(safeIdx + 1, totalQuestions - 1))}
                    disabled={safeIdx >= totalQuestions - 1}
                    className="text-xs border border-zinc-800 text-zinc-400 hover:text-zinc-100 disabled:opacity-40 disabled:hover:text-zinc-400 rounded-md px-3 py-1.5"
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="mt-4 space-y-3">
            <DeliverableFeedback
              deliverable="predict_questions"
              label="Predicted Questions"
              entityKey={activeSet.question_set_id}
              context={{ question_set_id: activeSet.question_set_id }}
            />
            {allPrepared && (
              <DeliverableFeedback
                deliverable="prepare_answers"
                label="Prepared Answers"
                entityKey={`${activeSet.question_set_id}_answers`}
                context={{ question_set_id: activeSet.question_set_id }}
              />
            )}
          </div>

          {/* The 15-question master bank is fixed (3 batches of 5) — it feeds
              Mock Interview and the Official AI Interview, so no ad-hoc growth. */}

          {/* Freemium locked teaser, only for free users when backend returns locked_count. */}
          {activeSet.locked_count > 0 && (
            <div
              data-testid="prep-locked-teaser"
              className="mt-4 rounded-xl border border-amber-500/30 bg-gradient-to-br from-amber-500/[0.05] via-zinc-900/40 to-zinc-950/60 p-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5"
            >
              <div className="h-9 w-9 rounded-full border border-amber-500/40 bg-amber-500/[0.08] flex items-center justify-center flex-shrink-0">
                <Lock className="h-3.5 w-3.5 text-amber-300" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-zinc-100 font-medium">
                  {activeSet.locked_count}+ additional personalized interview questions are ready.
                </div>
                <div className="text-[11px] text-zinc-500 mt-0.5 leading-relaxed">
                  Unlock unlimited personalized questions plus draft answers, follow-ups, and interview replay.
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  track("predicted.locked_teaser_clicked", {
                    locked_count: activeSet.locked_count,
                    total_available: activeSet.total_available,
                  });
                  setShowUpgrade(true);
                }}
                data-testid="prep-locked-cta"
                className="inline-flex items-center gap-1.5 text-xs font-semibold bg-amber-500 hover:bg-amber-400 text-amber-950 rounded-lg px-3.5 py-2 shadow-[0_0_20px_-6px_rgba(245,158,11,0.55)] flex-shrink-0 transition-colors"
              >
                Unlock all {activeSet.total_available}
                <ArrowRight className="h-3 w-3" />
              </button>
            </div>
          )}
        </div>
      )}

      <UpgradeLiveModal
        open={showUpgrade}
        onClose={() => setShowUpgrade(false)}
        onActivated={async () => {
          await refreshEntitlement();
          setShowUpgrade(false);
          // Reload the active set - paid users now receive the full list.
          if (activeSet?.question_set_id) {
            const { data } = await api.get(`/interview/questions/sets/${activeSet.question_set_id}`);
            setActiveSet(data);
          }
        }}
      />
    </div>
  );
}

/**
 * AnswerScoreChip - quality-score badge next to Save on each predicted
 * question. Premium users see the numeric score + coaching tip after saving.
 * Free users see a blurred number behind a lock; clicking routes to /app/upgrade.
 */
function AnswerScoreChip({ index, grade, busy, isFree, answerText, onUpgrade }) {
  const scoreClass = (score) => {
    if (score >= 85) return "text-emerald-300 border-emerald-500/40 bg-emerald-500/[0.08]";
    if (score >= 65) return "text-amber-300 border-amber-500/40 bg-amber-500/[0.08]";
    return "text-rose-300 border-rose-500/40 bg-rose-500/[0.08]";
  };

  if (isFree) {
    const hasContent = (answerText || "").trim().length >= 20;
    if (!hasContent) {
      return (
        <span
          data-testid={`prep-score-empty-${index}`}
          className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-600 font-mono"
        >
          <Gauge className="h-3 w-3" /> AI score
        </span>
      );
    }
    return (
      <button
        type="button"
        onClick={onUpgrade}
        data-testid={`prep-score-locked-${index}`}
        className="group inline-flex items-center gap-1.5 text-xs font-semibold rounded-md px-2.5 py-1.5 border border-amber-500/40 bg-amber-500/[0.06] hover:bg-amber-500/[0.12] transition-colors"
        aria-label="Unlock AI answer score"
      >
        <span
          aria-hidden
          className="inline-flex items-center gap-1 text-amber-300/90 select-none"
          style={{ filter: "blur(5px)" }}
        >
          <span className="tabular-nums">88</span>
          <span className="text-[10px]">/100</span>
        </span>
        <Lock className="h-3 w-3 text-amber-300" />
        <span className="text-[10px] uppercase tracking-widest text-amber-300 opacity-0 group-hover:opacity-100 transition-opacity">
          Unlock
        </span>
      </button>
    );
  }

  if (busy) {
    return (
      <span
        data-testid={`prep-score-busy-${index}`}
        className="inline-flex items-center gap-1.5 text-xs text-zinc-400 rounded-md px-2.5 py-1.5 border border-zinc-800 bg-zinc-900/60"
      >
        <Loader2 className="h-3 w-3 animate-spin" /> Grading…
      </span>
    );
  }

  if (!grade) {
    return (
      <span
        data-testid={`prep-score-empty-${index}`}
        className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-600 font-mono"
      >
        <Gauge className="h-3 w-3" /> Save to grade
      </span>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2 flex-1 min-w-0">
      <span
        data-testid={`prep-score-${index}`}
        className={`inline-flex items-center gap-1 text-xs font-semibold rounded-md px-2.5 py-1.5 border tabular-nums flex-shrink-0 ${scoreClass(grade.score)}`}
      >
        <Gauge className="h-3 w-3" />
        {grade.score}<span className="text-[10px] opacity-70">/100</span>
      </span>
      {grade.tip && (
        <span
          data-testid={`prep-score-tip-${index}`}
          className="text-[11px] text-zinc-400 leading-snug flex-1 min-w-0 basis-full sm:basis-auto sm:min-w-[200px] whitespace-normal break-words"
        >
          {grade.tip}
        </span>
      )}
    </div>
  );
}

